import { useState } from 'react'
import { OrderTraceKey } from '@/console/api/types'
import { useReportBusy } from '@/console/state/useReportBusy'
import { PageHeading } from '@/console/ui/PageHeading'
import { TABS } from '@/console/shell/tabs'
import { OrderSearchPanel } from './OrderSearchPanel'
import { RedispatchPanel } from './RedispatchPanel'
import { SkuBackfillPanel } from './SkuBackfillPanel'
import { SkuLookupPanel } from './SkuLookupPanel'
import { useManualRedispatch } from './useManualRedispatch'
import { useWorkflowRestart } from './useWorkflowRestart'
import { WorkflowRestartPanel } from './WorkflowRestartPanel'

const tab = TABS.find((t) => t.key === 'fopt')!

/**
 * LOGISPRD-12506 — F-OPT 탭. 재발사(`s-fopt-redispatch`/`s-fopt-result`) 한 패널만 조합한다.
 *
 * 범위 축소(2026-09-15, 사용자 결정) — 재고 대조(`s-fopt-compare`)·바코드 보정(`s-fopt-fix`)을
 * 뺐다. 근거는 `shell/tabs.ts` 상단 주석 참고.
 *
 * LOGISPRD-13034 — 주문 조회 패널을 맨 위에 붙였다. 재발사 제출 상태(`useManualRedispatch`)를 이
 * 탭이 들고 두 패널에 나눠 준다 — 조회에서 고른 행과 직접 입력한 id 가 같은 다이얼로그 · 같은
 * 잠금 · 같은 결과 섹션을 쓴다.
 *
 * 3차 리뷰(LOGISPRD-13034) — 재발사는 동기 호출 최대 5분에 자동 재시도가 없다. 그 사이 탭을 바꾸면
 * 이 탭이 언마운트되며 `submitting`·결과가 통째로 사라져, F-OPT 로 돌아와도 "아무것도 안 보낸 것"
 * 처럼 보여 중복 재발사를 부른다. `onBusyChange` 로 `submitting` 값이 바뀔 때만 셸에 알리고, 셸이
 * `TabBar` 를 잠가 탭 전환 자체를 막는다(제출 상태를 셸로 끌어올리는 큰 리팩터는 하지 않는다).
 *
 * LOGISPRD-15746 — 맨 아래에 워크플로 restart 패널을 붙였다. 호출이 최대 2분쯤 걸리고 응답을 놓치면 이어받을 run 을
 * 잃으므로 재발사와 같이 탭 전환을 막는다.
 */
export const FoptTab = ({ onTrace, onBusyChange }: { onTrace?: (key: OrderTraceKey) => void; onBusyChange?: (busy: boolean) => void }): JSX.Element => {
  const redispatch = useManualRedispatch()
  const restart = useWorkflowRestart()
  const [skuBackfillBusy, setSkuBackfillBusy] = useState(false)
  // 백필도 응답을 놓치면 되돌릴 근거(previousSkuId)를 잃으므로 재발사와 같이 탭 전환을 막는다.
  const busy = redispatch.submitting || skuBackfillBusy || restart.submitting

  useReportBusy(busy, onBusyChange)

  return (
    <>
      <PageHeading title={tab.heading} sub={tab.sub} />
      <OrderSearchPanel
        onRedispatch={(ids) => redispatch.ask(ids, 'search')}
        redispatchLocked={redispatch.submitting}
        redispatchedIds={redispatch.lastSubmittedIds}
        onTrace={onTrace}
      />
      <SkuLookupPanel />
      <SkuBackfillPanel onBusyChange={setSkuBackfillBusy} />
      <RedispatchPanel controller={redispatch} />
      <WorkflowRestartPanel controller={restart} />
    </>
  )
}
